import { useState, useEffect } from 'react';
import { jobApi, type Job } from '@src/api/client';

const JobsPage = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    jobApi
      .list({ limit: 100 })
      .then(data => setJobs(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this saved job?')) return;
    try {
      await jobApi.delete(id);
      setJobs(prev => prev.filter(j => j.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-200" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Saved Jobs</h1>
        <p className="text-gray-500">Jobs detected and saved from your browsing</p>
      </div>

      {jobs.length === 0 ? (
        <div className="rounded-xl bg-white p-12 text-center shadow-sm">
          <p className="text-gray-400">
            No saved jobs yet. Browse job sites with the Chrome extension to save jobs here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map(j => (
            <div key={j.id} className="rounded-xl bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{j.title}</h3>
                    {j.matchScore !== undefined && j.matchScore > 0 && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          j.matchScore >= 80
                            ? 'bg-green-100 text-green-700'
                            : j.matchScore >= 60
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                        {Math.round(j.matchScore)}% match
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {j.company}
                    {j.location ? ` - ${j.location}` : ''}
                  </p>
                  {j.salary && <p className="mt-0.5 text-sm text-green-600">{j.salary}</p>}
                  {j.tags && j.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {j.tags.slice(0, 8).map(tag => (
                        <span key={tag} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {j.missingSkills && j.missingSkills.length > 0 && (
                    <p className="mt-1 text-xs text-orange-500">Missing: {j.missingSkills.join(', ')}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">
                    {j.source && `From ${j.source} | `}Saved {new Date(j.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {j.url && (
                    <a
                      href={j.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
                      View
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(j.id)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsPage;
